/* eslint-disable no-unused-vars, react/prop-types, no-mixed-operators, eqeqeq */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, createStyles } from '@material-ui/core';
import { ArrowUpward as ArrowUpwardIcon } from '@material-ui/icons';
import { Button, Typography } from 'embeds/components';

const styles = ({
  spacing: { unit },
  custom: {
    fontSizes: { bodyG, bodyS },
  },
  palette: {
    common: { white },
  },
}) =>
  createStyles({
    button: {
      position: 'fixed',
      zIndex: 1000,
      borderRadius: `${unit * 2 + 4}px`,
      height: 33,
      left: '45%',
      marginTop: `${unit * 5}px`,
    },
    buttonIcon: {
      color: white,
      fontSize: bodyG,
    },
    buttonLabel: {
      color: white,
      fontSize: bodyS,
      fontWeight: 500,
      padding: `${unit}px`,
    },
  });

class InfiniteScroll extends React.Component {
  constructor(props) {
    super(props);

    this.scrollListener = this.scrollListener.bind(this);
    this.scrollToBegin = this.scrollToBegin.bind(this);

    this.state = {
      lastOffset: 250,
      scrolled: false,
      initialScrollTop: undefined,
    };
  }

  componentDidMount() {
    this.attachScrollListener();
  }

  componentDidUpdate(nextProps) {
    this.attachScrollListener();
  }

  componentWillUnmount() {
    this.detachScrollListener();
  }

  calculateTopPosition(el) {
    if (!el) {
      return 0;
    }

    return el.offsetTop + this.calculateTopPosition(el.offsetParent);
  }

  scrollToBegin() {
    const el = this.scrollComponent;
    const { isReverse } = this.props;

    el.scrollIntoView(!isReverse, { behavior: 'auto' });
  }

  scrollListener() {
    const {
      hasMore,
      isReverse,
      loadMore,
      onScrollEnd,
      onScrollStart,
      showNavigationButton,
      threshold,
      useWindow,
    } = this.props;
    const { initialScrollTop, lastOffset, scrolled } = this.state;
    const el = this.scrollComponent;
    const scrollEl = window;

    let offset;

    if (useWindow) {
      const scrollTop =
        scrollEl.pageYOffset !== undefined
          ? scrollEl.pageYOffset
          : (document.documentElement || document.body.parentNode || document.body).scrollTop;
      if (isReverse) {
        offset = scrollTop;
      } else {
        offset = this.calculateTopPosition(el) + el.offsetHeight - scrollTop - window.innerHeight;
      }
    } else if (isReverse) {
      offset = el.parentNode.scrollTop;
    } else {
      offset = el.scrollHeight - el.parentNode.scrollTop - el.parentNode.clientHeight;
    }

    if (initialScrollTop === undefined) {
      this.setState({ initialScrollTop: el.parentNode.scrollTop });
    } else if (isReverse && offset > initialScrollTop) {
      this.setState({
        initialScrollTop: el.parentNode.scrollHeight - el.parentNode.clientHeight,
      });
    } else {
      const scrolling = !isReverse
        ? initialScrollTop < el.parentElement.scrollTop
        : initialScrollTop > el.parentElement.scrollTop;

      if (!scrolled && scrolling) {
        if (onScrollStart) {
          onScrollStart();
        }
      }
      if (scrolled && !scrolling) {
        if (onScrollEnd) {
          onScrollEnd();
        }
      }

      if ((!scrolled && scrolling) || (scrolled && !scrolling)) {
        this.setState({ scrolled: scrolling });
      }
    }

    if (offset < Number(threshold) && offset !== lastOffset) {
      this.detachScrollListener();

      // Call loadMore after detachScrollListener to allow for non-async loadMore functions
      if (typeof loadMore === 'function' && hasMore) loadMore();

      this.setState({ lastOffset: offset });
    }
  }

  attachScrollListener() {
    const { hasMore, initialLoad, useCapture, useWindow } = this.props;

    if (!hasMore) this.detachScrollListener();

    let scrollEl = window;
    if (!useWindow) {
      scrollEl = this.scrollComponent.parentNode;
    }

    scrollEl.addEventListener('scroll', this.scrollListener, useCapture);
    scrollEl.addEventListener('resize', this.scrollListener, useCapture);

    if (initialLoad) {
      this.scrollListener();
    }
  }

  detachScrollListener() {
    const { useCapture, useWindow } = this.props;
    let scrollEl = window;

    // WORKAROUND the component might unmount before the scrollComponent is present
    if (!useWindow && this.scrollComponent) {
      scrollEl = this.scrollComponent.parentNode;
    }

    scrollEl.removeEventListener('scroll', this.scrollListener, useCapture);
    scrollEl.removeEventListener('resize', this.scrollListener, useCapture);
  }

  // Set a defaut loader for all your `InfiniteScroll` components
  setDefaultLoader(loader) {
    this._defaultLoader = loader;
  }

  render() {
    const { children, showNavigationButton, style, className } = this.props;
    const { scrolled } = this.state;

    return (
      <div
        className={className}
        style={style}
        ref={(node) => {
          this.scrollComponent = node;
        }}
      >
        {showNavigationButton && scrolled && this.renderNavigationButton()}
        {children}
      </div>
    );
  }

  renderNavigationButton() {
    const { navigationButtonLabel, classes } = this.props;

    return (
      <Button color="secondary" className={classes.button} onClick={this.scrollToBegin} variant="contained">
        <ArrowUpwardIcon className={classes.buttonIcon} />
        <Typography component="span" className={classes.buttonLabel}>
          {navigationButtonLabel}
        </Typography>
      </Button>
    );
  }
}

InfiniteScroll.propTypes = {
  hasMore: PropTypes.bool,
  initialLoad: PropTypes.bool,
  isReverse: PropTypes.bool,
  loadMore: PropTypes.func,
  navigationButtonLabel: PropTypes.string,
  onScrollEnd: PropTypes.func,
  onScrollStart: PropTypes.func,
  showNavigationButton: PropTypes.bool,
  threshold: PropTypes.number,
  useCapture: PropTypes.bool,
  useWindow: PropTypes.bool,
  className: PropTypes.string,
};

InfiniteScroll.defaultProps = {
  hasMore: false,
  initialLoad: true,
  isReverse: false,
  navigationButtonLabel: '',
  showNavigationButton: false,
  threshold: 250,
  useCapture: false,
  useWindow: true,
  className: '',
};

export default withStyles(styles)(InfiniteScroll);
